import * as React from "react";

import { cn } from "@/lib/utils";

import type { ChapterDefinition } from "./chapter-nav";
import { ChapterNav } from "./chapter-nav";

/**
 * PageRail — vertical rail-and-divider pattern. Desktop: chapter nav sits in
 * a sticky left aside. Mobile: chapter nav becomes a horizontal scroller
 * pinned just below the page nav (fin.ai pattern), and the content fills the
 * full width.
 *
 * On wide viewports (>1440px) the rail is capped at 1440px so the section
 * stops growing once the surrounding chrome does, with `#0d0d0d` from the
 * page bg filling the space on either side. The matching left/right rails
 * sit at the 1440px boundary on lg+.
 */
export function PageRail({
  chapters,
  children,
  className,
}: {
  chapters: ChapterDefinition[];
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative mx-auto w-full max-w-[1440px] bg-[#0d0d0d]",
        // Outer rails — only on lg+ where the chapter nav sidebar exists.
        // Below lg the layout collapses to a single column with no side
        // rails (matching the mobile chapter-nav scroller pattern).
        "lg:border-x lg:border-[#313335]",
        className
      )}
    >
      {/* Mobile: horizontal chapter nav pinned below action bar + page nav.
          The page nav itself is taller on `<md` (60px logo row + 48px nav-
          links row = 108px) and collapses to a single 60px row from md up,
          so the sticky top tracks both breakpoints. */}
      <div
        className={cn(
          "sticky z-30 bg-[#0d0d0d] lg:hidden",
          "top-[calc(var(--vs-action-h,36px)+108px)]",
          "md:top-[calc(var(--vs-action-h,36px)+60px)]"
        )}
      >
        <ChapterNav chapters={chapters} orientation="horizontal" />
      </div>

      <div className="relative grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)]">
        <aside className="relative hidden lg:block lg:border-r lg:border-[#313335]">
          <div className="sticky" style={{ top: "var(--vs-rail-top, 108px)" }}>
            <ChapterNav chapters={chapters} orientation="vertical" />
          </div>
        </aside>
        <div className="relative">{children}</div>
      </div>
    </div>
  );
}

export function PageRailSection({
  id,
  children,
  className,
  withBottomBorder = true,
}: {
  /**
   * Optional anchor id. The chapter-nav resolves its targets via the inner
   * section's own id (e.g. `id="speakers"` on `VsSpeakers`'s outermost div),
   * so this prop is only needed for ad-hoc deep links.
   */
  id?: string;
  children: React.ReactNode;
  className?: string;
  withBottomBorder?: boolean;
}) {
  return (
    <section
      id={id}
      className={cn(
        "relative scroll-mt-24",
        withBottomBorder && "border-b border-[#313335]",
        className
      )}
    >
      {children}
    </section>
  );
}
